import React from 'react'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { Box, createTheme, Stack, TableSortLabel, ThemeProvider } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { EFTypography } from '../EFTypography'
import { currentTheme, isDarkTheme } from '~/redux/features'
import { useAppSelector } from '~/app/hooks'
import classNames from 'classnames/bind'
import styles from './EFTable.module.scss'
import { camalize } from '~/utils'
import { EnhancedTableProps } from './EFTable.model'
import { EFTableCell } from './EFTableCell'
import { EFCheckbox } from '../EFCheckbox'

const cx = classNames.bind(styles)
export function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    selectAllCheckBox,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    showDescription,
    onRequestSort,
    headCells,
    blankCheckBox
  } = props

  const isDarkMode = useAppSelector(isDarkTheme)
  const theme = useAppSelector(currentTheme)

  const createSortHandler = (property: any) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }
  const TableHeadTheme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            '& .Mui-active': {
              color: isDarkMode ? '#a9a9a9 !important' : 'var(--dark)',
              '& svg': {
                color: isDarkMode ? '#a9a9a9 !important' : 'var(--dark)'
              }
            }
          }
        }
      }
    }
  })

  return (
    <TableHead
      sx={{
        '&& .MuiTableCell-root': {
          fontSize: 'var(--fs-md)',
          background: isDarkMode ? 'var(--header-table-dark)' : '#F6F7F9'
        }
      }}
    >
      <ThemeProvider theme={TableHeadTheme}>
        <TableRow>
          {selectAllCheckBox && (
            <EFTableCell padding='checkbox'>
              {!blankCheckBox && (
                <EFCheckbox
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={onSelectAllClick}
                />
              )}
            </EFTableCell>
          )}
          {headCells.map((headCell, index) => (
            <EFTableCell
              className={cx(
                'table-head-cell',
                `table-head-cell table-cell ${theme} ${
                  headCell.sticky ? 'stickyColumn' : ''
                }`,
                `${camalize(headCell.id)}-col`,
                headCell.stickyEnd ? 'stickyEnd' : ''
              )}
              key={headCell.id + index}
              align={headCell.align}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
              hidden={!headCell.display}
            >
              {headCell.id === 'more' ? (
                <div></div>
              ) : (
                <Stack>
                  <EFTypography fontWeight={500}>
                    {headCell.id !== '' && (
                      <TableSortLabel
                        className={cx('sort-label', theme)}
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={createSortHandler(headCell.id)}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <Box
                            component='span'
                            sx={visuallyHidden}
                          >
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    )}
                  </EFTypography>
                  {showDescription && (
                    <EFTypography
                      fontWeight={500}
                      fontSize='0.8rem'
                      pt={2}
                      height='2rem'
                    >
                      {headCell.description}
                    </EFTypography>
                  )}
                </Stack>
              )}
            </EFTableCell>
          ))}
        </TableRow>
      </ThemeProvider>
    </TableHead>
  )
}
